import { Button } from "@/components/Button";
import { Form } from "@/components/Form";
import { Input } from "@/components/Input";
import { H3 } from "@/components/Typography";
import { useForgotPassword } from "@/requests/hooks/user";
import formatError from "@/utils/formatError";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { InferType, object, string } from "yup";

const formSchema = object({
  email: string()
    .email(t("auth.forgotPassword.input.emailInvalid"))
    .required(t("auth.forgotPassword.input.requiredError")),
});

type IForgotPasswordForm = InferType<typeof formSchema>;

const ForgotPassword = () => {
  const { mutate, isPending, isSuccess, isError, error } = useForgotPassword();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: {
      errors: { email },
    },
    setError,
  } = useForm<IPostForgotPasswordRQ>({ resolver: yupResolver(formSchema) });

  useEffect(() => {
    if (isSuccess) {
      navigate("/auth/login");
      toast.success(t("auth.forgotPassword.success"));
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      const { codes } = formatError(error);

      if (codes?.includes("Email")) {
        setError("email", {
          message: t("auth.forgotPassword.input.emailInvalid"),
        });
      }
    }
  }, [isError]);

  const onSubmitForgotPassword = ({ email }: IForgotPasswordForm) => {
    mutate({ email });
  };
  return (
    <>
      <div className="relative">
        <Link to="/auth/login">
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="absolute left-0 top-10"
          />
        </Link>
        <H3 className="flex-grow text-center font-semibold">
          {t("auth.forgotPassword.title")}
        </H3>
      </div>
      <p className="mb-20 text-center text-gray-dark">
        {t("auth.forgotPassword.subtitle")}
      </p>
      <Form onSubmit={handleSubmit(onSubmitForgotPassword)}>
        <Input
          label={t("auth.forgotPassword.input.label")}
          {...register("email")}
          type="email"
          helperText={email?.message}
          variant={!!email?.message ? "error" : undefined}
        />
        <Button type="submit" width="unset" loading={isPending}>
          {t("auth.forgotPassword.button")}
        </Button>
      </Form>
    </>
  );
};

export default ForgotPassword;
